import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { uploadFileToBlobSas } from '@/lib/upload-media-to-blob.ts';
import {
  mediaUploadAbortKeepalive,
  mediaUploadComplete,
  mediaUploadHeartbeat,
  mediaUploadInitiate,
  mediaUploadPreflight,
  mediaUploadSequenceInitiate,
} from '@/state/mutations/media/media-upload-api.ts';
import {
  invalidateMediaUploadQueries,
  resolveMediaParentFromCache,
} from '@/state/mutations/media/invalidate-media-upload-queries.ts';
import { patchMediaUploadingAssetInQueryCache } from '@/state/mutations/media/patch-media-uploading-asset-cache.ts';
import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto.ts';
import type { MediaUploadPreflightResponse } from '@/types/dtos/media/media-upload.dto.ts';
import { ServerError } from '@/types/errors/server.error.ts';
import {
  createPreflightBlockedUploadError,
  isMediaUploadPreflightBlocked,
  shouldPreserveMediaUploadFeedback,
} from '@/helpers/media-upload-preflight.helper.ts';
import { abortMediaUploadSession } from '@/helpers/media-upload-session.helper.ts';
import { validateMediaUploadFileSize, validateMediaUploadFilesSize } from '@/helpers/media-upload-size-limit.helper.ts';

function surfaceUploadingAssetInCache({
  queryClient,
  mediaId,
  assetVersion,
  fileName,
}: {
  queryClient: QueryClient;
  mediaId: string;
  assetVersion: number;
  fileName: string;
}) {
  const parent = resolveMediaParentFromCache(queryClient, mediaId);
  const sessionOriginId = parent?.parentType.toLowerCase() === 'session' ? parent.parentId : undefined;
  patchMediaUploadingAssetInQueryCache(queryClient, {
    mediaId,
    sessionOriginId,
    asset: {
      version: assetVersion,
      name: fileName,
      uri: `/v1/media/${mediaId}/asset/${assetVersion}/download`,
      isApproved: false,
      uploadStatus: 'uploading',
      createdAt: new Date().toISOString(),
      createdBy: { id: 'uploading', firstName: '', lastName: '' } as MediaAssetDto['createdBy'],
    },
  });
  invalidateMediaUploadQueries(queryClient, mediaId);
}

export type MediaUploadStatus = 'idle' | 'validating' | 'blocked' | 'uploading' | 'completing' | 'success' | 'error';

export type MediaUploadItemProgress = {
  fileName: string;
  fileSizeBytes: number;
  progress: number;
  status: MediaUploadStatus | 'pending';
};

function throwIfStaleUpload(generation: number, currentGeneration: number): void {
  if (generation !== currentGeneration) {
    throw new MediaUploadError('Upload cancelled');
  }
}

export type MediaUploadQuotaBlocked = {
  kind: 'quota_blocked';
  preflight: MediaUploadPreflightResponse;
};

export class MediaUploadError extends Error {
  constructor(
    message: string,
    public readonly errorCode?: string,
    public readonly quotaBlocked?: MediaUploadQuotaBlocked,
  ) {
    super(message);
    this.name = 'MediaUploadError';
  }
}

const IMAGE_UPLOAD_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const UPLOAD_HEARTBEAT_MS = 20_000;

export function isImageUploadFile(file: File): boolean {
  if (IMAGE_UPLOAD_MIME_TYPES.has(file.type)) {
    return true;
  }
  return /\.(jpe?g|png|webp)$/i.test(file.name);
}

export function useMediaUpload() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadContextRef = useRef<{ mediaId: string; uploadId: string } | null>(null);
  const uploadGenerationRef = useRef(0);
  const tokenRef = useRef<string | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current != null) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(
    (mediaId: string, uploadId: string) => {
      stopHeartbeat();
      const tick = () => {
        void (async () => {
          if (uploadContextRef.current?.uploadId !== uploadId) {
            return;
          }
          const token = await getToken();
          if (!token || uploadContextRef.current?.uploadId !== uploadId) {
            return;
          }
          tokenRef.current = token;
          try {
            await mediaUploadHeartbeat(mediaId, { uploadId }, token);
          } catch {
            // A missed heartbeat is recovered by the next tick. Quiet sessions expire on the server.
          }
        })();
      };
      tick();
      heartbeatTimerRef.current = window.setInterval(tick, UPLOAD_HEARTBEAT_MS);
    },
    [getToken, stopHeartbeat],
  );

  const [status, setStatus] = useState<MediaUploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<MediaUploadError | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [preflight, setPreflight] = useState<MediaUploadPreflightResponse | null>(null);
  const [uploadItems, setUploadItems] = useState<MediaUploadItemProgress[]>([]);

  const abortActiveUploadSession = useCallback(async (): Promise<string | undefined> => {
    stopHeartbeat();
    const ctx = uploadContextRef.current;
    uploadContextRef.current = null;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (!ctx) {
      return undefined;
    }
    await abortMediaUploadSession(ctx, getToken);
    invalidateMediaUploadQueries(queryClient, ctx.mediaId);
    return ctx.mediaId;
  }, [getToken, queryClient, stopHeartbeat]);

  useEffect(() => {
    // Reload and tab close. Cleanup must not abort: this hook also lives in a panel that unmounts.
    const abandonOnUnload = (event: PageTransitionEvent) => {
      if (event.persisted) {
        return;
      }
      stopHeartbeat();
      const ctx = uploadContextRef.current;
      const token = tokenRef.current;
      if (!ctx || !token) {
        return;
      }
      uploadContextRef.current = null;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      mediaUploadAbortKeepalive(ctx.mediaId, ctx.uploadId, token);
    };
    window.addEventListener('pagehide', abandonOnUnload);
    return () => {
      window.removeEventListener('pagehide', abandonOnUnload);
    };
  }, [stopHeartbeat]);

  const clearUploadState = useCallback(() => {
    uploadGenerationRef.current += 1;
    setStatus('idle');
    setProgress(0);
    setError(null);
    setFileName(null);
    setFileSizeBytes(null);
    setPreflight(null);
    setUploadItems([]);
  }, []);

  const reset = useCallback(() => {
    void abortActiveUploadSession();
    clearUploadState();
  }, [abortActiveUploadSession, clearUploadState]);

  const resetAfterUploadAttempt = useCallback(
    (error?: unknown) => {
      if (error != null && shouldPreserveMediaUploadFeedback(error)) {
        return;
      }
      reset();
    },
    [reset],
  );

  const rejectPreflight = useCallback(
    (
      file: File,
      preflightResult: MediaUploadPreflightResponse,
      display?: { fileName: string; fileSizeBytes: number },
    ): never => {
      const displayName = display?.fileName ?? preflightResult.details?.fileName ?? file.name;
      const displaySize = display?.fileSizeBytes ?? preflightResult.requiredBytes ?? file.size;

      setFileName(displayName);
      setFileSizeBytes(displaySize);
      setPreflight(preflightResult);
      setStatus('blocked');
      setUploadItems([
        {
          fileName: preflightResult.details?.fileName ?? file.name,
          fileSizeBytes: preflightResult.requiredBytes ?? file.size,
          progress: 0,
          status: 'blocked',
        },
      ]);
      throw createPreflightBlockedUploadError(
        preflightResult,
        preflightResult.requiredBytes ?? file.size,
        preflightResult.details?.fileName ?? file.name,
      );
    },
    [],
  );

  const cancel = useCallback(async () => {
    await abortActiveUploadSession();
    clearUploadState();
  }, [abortActiveUploadSession, clearUploadState]);

  const releaseFailedUploadSession = useCallback(
    async (generation: number) => {
      if (generation !== uploadGenerationRef.current) {
        return;
      }
      await abortActiveUploadSession();
    },
    [abortActiveUploadSession],
  );

  const rejectLocalSizeLimit = useCallback(
    (file: File, error: MediaUploadError, sequenceFiles?: File[]): never => {
      const preflightResult = error.quotaBlocked?.preflight;
      if (preflightResult) {
        const display =
          sequenceFiles && sequenceFiles.length > 1
            ? {
                fileName: `${sequenceFiles.length} images`,
                fileSizeBytes: sequenceFiles.reduce((sum, item) => sum + item.size, 0),
              }
            : undefined;
        const contextFile = sequenceFiles?.find((item) => item.name === preflightResult.details?.fileName) ?? file;
        rejectPreflight(contextFile, preflightResult, display);
      }
      throw error;
    },
    [rejectPreflight],
  );

  const upload = useCallback(
    async (mediaId: string, file: File): Promise<MediaAssetDto> => {
      const localSizeCheck = validateMediaUploadFileSize(file);
      if (!localSizeCheck.ok) {
        rejectLocalSizeLimit(file, localSizeCheck.error);
      }

      await abortActiveUploadSession();
      clearUploadState();
      const generation = uploadGenerationRef.current;
      setFileName(file.name);
      setFileSizeBytes(file.size);
      setUploadItems([{ fileName: file.name, fileSizeBytes: file.size, progress: 0, status: 'validating' }]);
      setStatus('validating');
      setProgress(0);

      const token = await getToken();
      if (!token) {
        throw new MediaUploadError('Not authenticated');
      }
      tokenRef.current = token;
      try {
        const preflightResult = await mediaUploadPreflight(
          mediaId,
          {
            fileSizeBytes: file.size,
            mimeType: file.type || undefined,
            fileName: file.name,
          },
          token,
        );

        throwIfStaleUpload(generation, uploadGenerationRef.current);

        if (isMediaUploadPreflightBlocked(preflightResult, file.size)) {
          rejectPreflight(file, preflightResult);
        }

        setPreflight(preflightResult);
        setStatus('uploading');
        setUploadItems([{ fileName: file.name, fileSizeBytes: file.size, progress: 0, status: 'uploading' }]);

        throwIfStaleUpload(generation, uploadGenerationRef.current);

        const initiate = await mediaUploadInitiate(
          mediaId,
          {
            declaredSizeBytes: file.size,
            fileName: file.name,
            mimeType: file.type || undefined,
          },
          token,
        );

        throwIfStaleUpload(generation, uploadGenerationRef.current);

        uploadContextRef.current = { mediaId, uploadId: initiate.uploadId };
        startHeartbeat(mediaId, initiate.uploadId);
        // Surface the in-flight asset row immediately — refetch alone can lose to stale approved playlist embeds.
        surfaceUploadingAssetInCache({
          queryClient,
          mediaId,
          assetVersion: initiate.assetVersion,
          fileName: file.name,
        });
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        await uploadFileToBlobSas({
          sasUrl: initiate.sasUrl,
          file,
          blockSize: initiate.recommendedBlockSize,
          abortSignal: abortController.signal,
          onProgress: (loaded, total) => {
            if (generation !== uploadGenerationRef.current) {
              return;
            }
            if (total > 0) {
              const nextProgress = Math.min(100, Math.round((loaded / total) * 100));
              setProgress(nextProgress);
              setUploadItems([
                { fileName: file.name, fileSizeBytes: file.size, progress: nextProgress, status: 'uploading' },
              ]);
            }
          },
        });

        throwIfStaleUpload(generation, uploadGenerationRef.current);

        setStatus('completing');
        setProgress(100);
        setUploadItems([{ fileName: file.name, fileSizeBytes: file.size, progress: 100, status: 'completing' }]);
        const completeToken = await getToken();
        if (!completeToken) {
          throw new MediaUploadError('Not authenticated');
        }
        const asset = await mediaUploadComplete(mediaId, { uploadId: initiate.uploadId }, completeToken);

        stopHeartbeat();
        invalidateMediaUploadQueries(queryClient, mediaId);
        uploadContextRef.current = null;
        setStatus('success');
        setUploadItems([{ fileName: file.name, fileSizeBytes: file.size, progress: 100, status: 'success' }]);
        return asset;
      } catch (err) {
        if (generation !== uploadGenerationRef.current) {
          throw err;
        }
        if (shouldPreserveMediaUploadFeedback(err)) {
          throw err;
        }
        await releaseFailedUploadSession(generation);
        const uploadErr =
          err instanceof MediaUploadError
            ? err
            : err instanceof ServerError
              ? new MediaUploadError(err.message, err.errorCode)
              : new MediaUploadError(err instanceof Error ? err.message : 'Upload failed');
        setError(uploadErr);
        setStatus('error');
        setUploadItems((items) =>
          items.map((item) => (item.status === 'success' ? item : { ...item, status: 'error' as const })),
        );
        throw uploadErr;
      }
    },
    [
      getToken,
      queryClient,
      rejectPreflight,
      rejectLocalSizeLimit,
      releaseFailedUploadSession,
      abortActiveUploadSession,
      clearUploadState,
      startHeartbeat,
      stopHeartbeat,
    ],
  );

  const uploadSequence = useCallback(
    async (mediaId: string, files: File[]): Promise<MediaAssetDto> => {
      const localSizeCheck = validateMediaUploadFilesSize(files);
      if (!localSizeCheck.ok) {
        const violatingName = localSizeCheck.error.quotaBlocked?.preflight.details?.fileName;
        const violatingFile = violatingName ? files.find((file) => file.name === violatingName) : undefined;
        rejectLocalSizeLimit(violatingFile ?? files[0], localSizeCheck.error, files);
      }

      await abortActiveUploadSession();
      clearUploadState();
      const generation = uploadGenerationRef.current;
      const orderedFiles = [...files];
      const totalBytes = orderedFiles.reduce((sum, file) => sum + file.size, 0);
      const label = `${orderedFiles.length} images`;

      setFileName(label);
      setFileSizeBytes(totalBytes);
      setUploadItems(
        orderedFiles.map((file) => ({
          fileName: file.name,
          fileSizeBytes: file.size,
          progress: 0,
          status: 'pending' as const,
        })),
      );
      setStatus('validating');

      const token = await getToken();
      if (!token) {
        throw new MediaUploadError('Not authenticated');
      }
      tokenRef.current = token;
      try {
        const preflightResult = await mediaUploadPreflight(
          mediaId,
          { fileSizeBytes: totalBytes, fileName: orderedFiles[0].name },
          token,
        );

        throwIfStaleUpload(generation, uploadGenerationRef.current);

        if (isMediaUploadPreflightBlocked(preflightResult, totalBytes)) {
          rejectPreflight(orderedFiles[0], preflightResult);
        }

        setPreflight(preflightResult);
        setStatus('uploading');
        const initiate = await mediaUploadSequenceInitiate(
          mediaId,
          {
            files: orderedFiles.map((file, index) => ({
              order: index + 1,
              fileName: file.name,
              mimeType: file.type || undefined,
              declaredSizeBytes: file.size,
            })),
          },
          token,
        );

        uploadContextRef.current = { mediaId, uploadId: initiate.uploadId };
        startHeartbeat(mediaId, initiate.uploadId);
        // Surface the in-flight asset row immediately — refetch alone can lose to stale approved playlist embeds.
        surfaceUploadingAssetInCache({
          queryClient,
          mediaId,
          assetVersion: initiate.assetVersion,
          fileName: orderedFiles[0]?.name ?? `sequence-v${initiate.assetVersion}`,
        });
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const framesByOrder = new Map(initiate.frames.map((frame) => [frame.order, frame]));
        let uploadedBytes = 0;

        for (let index = 0; index < orderedFiles.length; index += 1) {
          const file = orderedFiles[index];
          const frame = framesByOrder.get(index + 1);
          if (!frame) {
            throw new MediaUploadError(`Missing upload URL for frame ${index + 1}`);
          }

          setUploadItems((items) =>
            items.map((item, itemIndex) =>
              itemIndex === index ? { ...item, status: 'uploading' as const, progress: 0 } : item,
            ),
          );

          await uploadFileToBlobSas({
            sasUrl: frame.sasUrl,
            file,
            blockSize: initiate.recommendedBlockSize,
            abortSignal: abortController.signal,
            onProgress: (loaded) => {
              const fileProgress = file.size > 0 ? Math.min(100, Math.round((loaded / file.size) * 100)) : 0;
              const aggregateProgress =
                totalBytes > 0 ? Math.min(100, Math.round(((uploadedBytes + loaded) / totalBytes) * 100)) : 0;
              setProgress(aggregateProgress);
              setUploadItems((items) =>
                items.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, progress: fileProgress, status: 'uploading' as const } : item,
                ),
              );
            },
          });

          uploadedBytes += file.size;
          setUploadItems((items) =>
            items.map((item, itemIndex) =>
              itemIndex === index ? { ...item, progress: 100, status: 'success' as const } : item,
            ),
          );
        }

        setStatus('completing');
        setProgress(100);
        setUploadItems((items) => items.map((item) => ({ ...item, progress: 100, status: 'completing' as const })));
        const completeToken = await getToken();
        if (!completeToken) {
          throw new MediaUploadError('Not authenticated');
        }
        const asset = await mediaUploadComplete(mediaId, { uploadId: initiate.uploadId }, completeToken);

        stopHeartbeat();
        invalidateMediaUploadQueries(queryClient, mediaId);
        uploadContextRef.current = null;
        setStatus('success');
        setUploadItems((items) => items.map((item) => ({ ...item, progress: 100, status: 'success' as const })));
        return asset;
      } catch (err) {
        if (generation !== uploadGenerationRef.current) {
          throw err;
        }
        if (shouldPreserveMediaUploadFeedback(err)) {
          throw err;
        }
        await releaseFailedUploadSession(generation);
        const uploadErr =
          err instanceof MediaUploadError
            ? err
            : new MediaUploadError(err instanceof Error ? err.message : 'Upload failed');
        setError(uploadErr);
        setStatus('error');
        setUploadItems((items) =>
          items.map((item) => (item.status === 'success' ? item : { ...item, status: 'error' as const })),
        );
        throw uploadErr;
      }
    },
    [
      getToken,
      queryClient,
      rejectPreflight,
      rejectLocalSizeLimit,
      releaseFailedUploadSession,
      abortActiveUploadSession,
      clearUploadState,
      startHeartbeat,
      stopHeartbeat,
    ],
  );

  const uploadFiles = useCallback(
    async (mediaId: string, files: File[]): Promise<MediaAssetDto> => {
      const selected = [...files];
      if (selected.length === 0) {
        throw new MediaUploadError('No files selected');
      }
      if (selected.length === 1) {
        return upload(mediaId, selected[0]);
      }
      if (!selected.every(isImageUploadFile)) {
        throw new MediaUploadError('When selecting multiple files, all must be images (JPEG, PNG, or WebP)');
      }
      return uploadSequence(mediaId, selected);
    },
    [upload, uploadSequence],
  );

  return {
    upload,
    uploadFiles,
    cancel,
    reset,
    resetAfterUploadAttempt,
    status,
    progress,
    error,
    fileName,
    fileSizeBytes,
    preflight,
    uploadItems,
    isBusy:
      status === 'validating' ||
      status === 'uploading' ||
      status === 'completing' ||
      status === 'blocked' ||
      status === 'success',
    isFeedbackVisible: status !== 'idle' && uploadItems.length > 0,
  };
}
